import React, { useContext, useState } from 'react';

import { Row, Col, Segmented, Alert } from 'antd';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';

import styled from 'styled-components';

import { getMoment } from '@libs/NwMoment';
import { getNow, getTime } from '@libs/NwMoment';

import { ListContext } from '@contexts/ListContext';

import { getContactName } from '@hooks/API/Contact/UseContactsList';
import { useAddFollowup } from '@hooks/API/Event/UseFollowup';
import useNwBreakPoints from '@hooks/Layout/UseNwBreakPoints';

import { NwCancelButton, NwSaveButton } from '@components/Gui/NwButton';
import {
    NWContactDropdown,
    NWFollowUpLabel,
    NWTextAreaCharCounter,
    NWProjectSelector,
    NWInput,
    NWSwitch,
    NWLongEventPeriodPicker,
    NWMultipleDateSelector,
    NWRecurrenceSelector,
} from '@components/Gui/NwForm/NwFormItems';
import { NwForm, NwFormButtonsBar } from '@components/Gui/NwForm/NwFormWrapper';

import { useFollowUpState } from './FollowUpContext';

const FollowUpFormNew = ({ allDay, dateSelected, onTouchForm, onUpdateFollowupData, eventInfo }) => {
    const { afterUpdate, modelId, projectId, onCancel } = useFollowUpState();
    const breakpoints = useNwBreakPoints();
    const { cachedList } = useContext(ListContext);
    const followUpLabels = cachedList.followUpLabels.items || [];
    const followUpLabelCode = eventInfo ? eventInfo.followUpLabelCode : '';
    const defaultFollowUp = followUpLabelCode
        ? followUpLabels.find((followupLabel) => followupLabel.Code === followUpLabelCode)
        : followUpLabels.find((followupLabel) => followupLabel.Default);
    const { mutateAsync: onAddFollowup } = useAddFollowup();
    const [errorMessage, setErrorMessage] = useState('');

    const datesModeOptions = [
        { label: 'Period', value: 'period' },
        { label: 'Multiple Dates', value: 'multipleDates' },
        { label: 'Recurrence', value: 'recurrence' },
    ];

    const initialValues = {
        Acknowledgment: 'NotChecked',
        AcknowledgmentName: '',
        ModelID: modelId,
        ProjectID: projectId,
        ServiceID: null,
        Description: '',
        StartDate: null,
        EndDate: null,
        FollowUpLabelCode: followUpLabelCode ? followUpLabelCode : defaultFollowUp ? defaultFollowUp.Code : '',
        Title: '',
        DatesMode: 'period',
        Period: {
            startDate: dateSelected ? getMoment(dateSelected).startOf('day') : getNow(true),
            endDate: dateSelected ? getMoment(dateSelected).startOf('day') : getNow(true),
            startTime: getTime(
                dateSelected
                    ? allDay
                        ? getMoment(dateSelected).startOf('day')
                        : dateSelected
                    : getNow().hour(9).minute(0)
            ),
            endTime: getTime(
                dateSelected
                    ? allDay
                        ? getMoment(dateSelected).endOf('day')
                        : dateSelected.clone()
                    : getNow().hour(9).minute(0)
            ),
            allDay: allDay || false,
            dates: [],
        },
        MultipleDates: {
            dates: dateSelected ? [getMoment(dateSelected).startOf('day').toDate()] : [],
            startTime: [0, 0],
            endTime: [23, 59],
            allDay: true,
        },
        Recurrence: {
            startDate: dateSelected ? getMoment(dateSelected).startOf('day') : getNow(true),
            endDate: dateSelected
                ? getMoment(dateSelected).startOf('day').add(6, 'months')
                : getNow(true).add(6, 'months'),
            every: 1,
            daysOfWeek: [],
            startTime: [0, 0],
            endTime: [23, 59],
            allDay: true,
        },
        ModelNotAvailable: defaultFollowUp ? defaultFollowUp.ModelNotAvailable : true,
    };

    const validationSchema = Yup.object().shape({
        Title: Yup.string().required('Title is required'),
        ModelID: Yup.string().required('Model is required'),
        FollowUpLabelCode: Yup.string().required('Follow up label is required'),
    });

    const submitForm = async (values, setStatus, setSubmitting) => {
        let periods = [];
        let recursion = null;

        if (values.DatesMode === 'period') {
            const sd = getMoment(values.Period.startDate)
                .hour(values.Period.startTime[0])
                .minute(values.Period.startTime[1]);
            const ed = getMoment(values.Period.endDate).hour(values.Period.endTime[0]).minute(values.Period.endTime[1]);
            periods = [{ StartDate: sd, EndDate: ed }];
        } else if (values.DatesMode === 'multipleDates') {
            // Multiple dates mode
            const { dates, startTime, endTime } = values.MultipleDates;
            periods = dates.map((date) => {
                const startDateTime = getMoment(date).hour(startTime[0]).minute(startTime[1]);
                const endDateTime = getMoment(date).hour(endTime[0]).minute(endTime[1]);
                return { StartDate: startDateTime, EndDate: endDateTime };
            });
        } else if (values.DatesMode === 'recurrence') {
            // Recurrence mode
            const { startDate, endDate, every, daysOfWeek, startTime, endTime } = values.Recurrence;
            if (daysOfWeek.length === 0) {
                setErrorMessage('Please select at least one day of the week');
                setSubmitting(false);
                return;
            }
            const sd = getMoment(startDate).hour(startTime[0]).minute(startTime[1]);
            const ed = getMoment(endDate).hour(endTime[0]).minute(endTime[1]);
            periods = [{ StartDate: sd, EndDate: ed }];
            recursion = {
                Type: 'Weekly',
                Every: every,
                DaysOfWeek: daysOfWeek,
            };
        }

        if (periods.length === 0) {
            setErrorMessage('No dates selected');
            setSubmitting(false);
            return;
        }

        const {
            Period: _period,
            StartDate: _startDate,
            EndDate: _endDate,
            DatesMode: _datesMode,
            MultipleDates: _multipleDates,
            Recurrence: _recurrence,
            ...followUpDataWithoutPeriod
        } = {
            ...initialValues,
            ...values,
        };

        const newFollowUpData = {
            ...followUpDataWithoutPeriod,
            Periods: periods,
            ...(recursion && { Recursion: recursion }),
        };

        try {
            const response = await onAddFollowup({ followupData: newFollowUpData });
            onTouchForm(false);
            if (afterUpdate) {
                afterUpdate({
                    ...response,
                    TypeName: 'FollowUp',
                });
            }
            onUpdateFollowupData(response.ID);
            handleCancel();
        } catch (error) {
            console.error('Add FollowUp Error => ', error);
        }
        setSubmitting(false);
    };

    const beforeSubmit = async (values, setStatus, setSubmitting) => {
        await submitForm(values, setStatus, setSubmitting);
        if (onTouchForm) {
            onTouchForm(false);
        }
    };

    const handleCancel = () => {
        if (onTouchForm) {
            onTouchForm(false);
        }
        onCancel();
    };

    const handleFollowUpLabelChange = (values, setFieldValue, value) => {
        const followup = followUpLabels.find((item) => item.Code === value);
        if (!values.Title) {
            setFieldValue(
                'Title',
                `${followup ? followup.Label : ''}${values.ServiceID ? ` - ${getContactName(values.ServiceID)}` : ''}`
            );
        }
        setFieldValue('ModelNotAvailable', followup && followup.ModelNotAvailable);
    };

    const handleServiceChange = (values, setFieldValue, value) => {
        if (!values.Title) {
            const followup = followUpLabels.find((item) => item.Code === values.FollowUpLabelCode);
            setFieldValue('Title', `${followup.Label} - ${getContactName(value)}`);
        }
    };

    const getColSpan = (defaultSpan = 12) => {
        return breakpoints === 1 ? 24 : defaultSpan;
    };

    return (
        <div className="event-form">
            <Formik
                initialValues={initialValues}
                enableReinitialize={false}
                validationSchema={validationSchema}
                onSubmit={(values, { setStatus, setSubmitting }) => {
                    setStatus();
                    beforeSubmit(values, setStatus, setSubmitting);
                }}
            >
                {({ isSubmitting, values, setFieldValue, handleSubmit }) => (
                    <NwForm small values={values} onTouchForm={onTouchForm} onFinish={handleSubmit} layout="vertical">
                        <Row gutter={10}>
                            {/* Project Selector */}
                            <Col span={24}>
                                <Field
                                    component={NWProjectSelector}
                                    name="ProjectID"
                                    value={values.ProjectID}
                                    readOnly={!!projectId}
                                />
                            </Col>

                            {/* Model Dropdown - only when modelId is not provided */}
                            {!modelId && (
                                <Col span={24}>
                                    <Field
                                        component={NWContactDropdown}
                                        label="Model"
                                        name="ModelID"
                                        value={values.ModelID}
                                        activeTypes={['mod']}
                                        activableTypes={[]}
                                        required
                                    />
                                </Col>
                            )}

                            {/* Activity Type */}
                            <Col span={getColSpan()}>
                                <Field
                                    component={NWFollowUpLabel}
                                    label="Activity Type"
                                    name="FollowUpLabelCode"
                                    value={values.FollowUpLabelCode}
                                    onAfterChange={(value) => handleFollowUpLabelChange(values, setFieldValue, value)}
                                />
                            </Col>

                            {/* Service */}
                            <Col span={getColSpan()}>
                                <Field
                                    component={NWContactDropdown}
                                    label="Service"
                                    name="ServiceID"
                                    value={values.ServiceID}
                                    activeTypes={['srv']}
                                    activableTypes={[]}
                                    onAfterChange={(value) => handleServiceChange(values, setFieldValue, value)}
                                />
                            </Col>

                            {/* Dates Mode Selector */}
                            <Col span={24} style={{ marginTop: '2rem', marginBottom: '1rem' }}>
                                <Segmented
                                    value={values.DatesMode}
                                    options={datesModeOptions}
                                    onChange={(value) => setFieldValue('DatesMode', value)}
                                />
                            </Col>

                            {/* Period - shown when DatesMode is 'period' */}
                            {values.DatesMode === 'period' && (
                                <Col span={24} style={{ marginBottom: '2rem' }}>
                                    <Field
                                        component={NWLongEventPeriodPicker}
                                        label=""
                                        name="Period"
                                        value={values.Period}
                                    />
                                </Col>
                            )}

                            {/* Multiple Dates - shown when DatesMode is 'multipleDates' */}
                            {values.DatesMode === 'multipleDates' && (
                                <Col span={24}>
                                    <Field
                                        component={NWMultipleDateSelector}
                                        label=""
                                        name="MultipleDates"
                                        value={values.MultipleDates}
                                    />
                                </Col>
                            )}

                            {/* Recurrence - shown when DatesMode is 'recurrence' */}
                            {values.DatesMode === 'recurrence' && (
                                <Col span={24}>
                                    <Field
                                        component={NWRecurrenceSelector}
                                        label=""
                                        name="Recurrence"
                                        value={values.Recurrence}
                                    />
                                </Col>
                            )}

                            {/* Title */}
                            <Col span={24}>
                                <Field
                                    component={NWInput}
                                    label="Title"
                                    name="Title"
                                    type="text"
                                    value={values.Title}
                                />
                            </Col>

                            {/* Description */}
                            <Col span={24}>
                                <Field
                                    component={NWTextAreaCharCounter}
                                    label="Description"
                                    name="Description"
                                    type="text"
                                    value={values.Description}
                                    maxLength={2000}
                                />
                            </Col>

                            {/* Model Not Available Switch */}
                            <Col span={24}>
                                <div style={{ margin: '1rem 0 2rem 0' }}>
                                    <Field
                                        component={NWSwitch}
                                        text="set model as not available"
                                        name="ModelNotAvailable"
                                        value={values.ModelNotAvailable}
                                        required
                                    />
                                </div>
                            </Col>
                        </Row>
                        <NwFormButtonsBar
                            left={<NwCancelButton disabled={isSubmitting} onClick={handleCancel} />}
                            right={<NwSaveButton htmlType="submit" disabled={isSubmitting} loading={isSubmitting} />}
                        />
                        {errorMessage && <Alert message={errorMessage} type="error" style={{ marginTop: '1rem' }} />}
                    </NwForm>
                )}
            </Formik>
        </div>
    );
};

export default React.memo(FollowUpFormNew);
